/**
 * The on-disk cache for decoded transaction bodies, and its eraser.
 *
 * Bodies are content-addressed by their S3 etag, so caching them in IndexedDB is
 * free correctness-wise: a re-decode mints a new etag and a new key, and stale
 * data is never served. What it is NOT free of is a privacy obligation. A shared
 * or handed-over browser would otherwise keep one user's decoded transactions on
 * disk for whoever signs in next, and unlike a token they never expire.
 *
 * So the cache and the code that erases it live in the same file, and the eraser
 * is wired into `SessionManager.onIdentityChange` — the one path every sign-out
 * and tenant switch goes through — rather than into the buttons that trigger
 * them. See {@link installTxCacheTeardown}.
 *
 * Identity comes from the live routing token rather than React, because the
 * teardown runs during sign-out, when the provider tree is on its way out.
 * Mirrors `cdn.ts`, which resolves its scope the same way and for the same
 * reason.
 */
import {
  createScopedTxCache,
  clearCdnSession,
  type ScopedTxCache,
  type StorageIdentity,
} from "@authe/fe-commons/storage";
import { tenantContextFromClaims } from "@authe/fe-commons/auth";
import { appEnv, APPLICATION_SLUG } from "@/config";
import { getRoutingToken } from "./routing-token";

function currentIdentity(): StorageIdentity {
  const token = getRoutingToken();
  const t = token
    ? tenantContextFromClaims(token as Record<string, unknown>, appEnv.claimsNamespace)
    : null;
  return {
    app: APPLICATION_SLUG,
    customerId: t?.customerId ?? null,
    orgId: t?.orgId ?? null,
    tenantId: t?.tenantId ?? null,
    userId: t?.userId ?? null,
  };
}

/**
 * The cache for the identity currently signed in.
 *
 * Rebuilt per call rather than memoised: the identity changes under us on a
 * tenant switch, and a cache pinned to the identity that happened to be current
 * at module load would write the new tenant's bodies under the old tenant's key
 * prefix. Construction is a couple of string joins over a shared IndexedDB
 * handle, so there is nothing to save by holding onto one.
 */
export function txCache(): ScopedTxCache {
  return createScopedTxCache(currentIdentity());
}

/**
 * Erase the cached bodies an identity change makes unreadable-by-policy.
 *
 * A sign-out clears the `user` tier and everything narrower — the departing
 * user's bodies, full stop. A tenant switch clears only `tenant`, because the
 * user is the same person and their own preferences should follow them; it is
 * the tenant's DATA that must not cross.
 *
 * Also drops the signed-CDN session, so the next read mints cookies against the
 * new identity instead of reusing a decision made for the old one.
 */
export async function clearTxCache(reason: "logout" | "tenant-switch"): Promise<void> {
  await txCache().clear(reason === "logout" ? "user" : "tenant");
  clearCdnSession();
}
